import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';

import ptBRLocale from 'date-fns/locale/pt-BR';
import { isSameDay, parseISO, format } from 'date-fns';
import DateFnsUtils from '@date-io/date-fns';
import { MuiPickersUtilsProvider, DatePicker } from '@material-ui/pickers';
import Backdrop from '~/components/BackdropComponent';
import CircularProgress from '@material-ui/core/CircularProgress';
import InfusionItem from './components/InfusionItem';
import InfusionShow from './components/InfusionShow';
import { errorMessage } from '~/utils/Message';

import { createTheme, ThemeProvider } from '@material-ui/core/styles';
import { calendarInfusions, calendarShow } from '~/services/infusions';

import { Container, Content, Tag, Subtitle, List } from './styles';

const theme = createTheme({
  palette: {
    primary: {
      main: '#0384ac',
    },
  },
});

function InfusionCalendar({ user }) {
  const [infusions, setInfusions] = useState([]);
  const [dates, setDates] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [loading, setLoading] = useState(false);

  const [active, setActive] = useState(null);
  const { id } = user;

  const loadData = useCallback(
    async (date) => {
      setInfusions([]);
      setDates([]);
      setActive(null);
      setLoading(true);

      try {
        const response = await calendarInfusions({ date, userId: id });

        setInfusions(response.infusions);
        setDates(response.dates);
      } catch (error) {
        errorMessage(error);
      } finally {
        setLoading(false);
      }
    },
    [id]
  );

  const handleSelectedDate = useCallback(
    async (date) => {
      setInfusions([]);
      setLoading(true);

      try {
        const response = await calendarShow({
          date: format(new Date(date), 'yyyy-MM-dd'),
          userId: id,
        });

        setInfusions(response.infusions);
      } finally {
        setLoading(false);
      }
    },
    [id]
  );

  useEffect(() => {
    loadData(new Date());
  }, [loadData]);

  useEffect(() => {
    if (!selectedDate) return;

    handleSelectedDate(selectedDate);
  }, [selectedDate, handleSelectedDate]);

  return (
    <Container>
      <Content>
        <MuiPickersUtilsProvider utils={DateFnsUtils} locale={ptBRLocale}>
          <ThemeProvider theme={theme}>
            <DatePicker
              loadingIndicator={<span />}
              value={selectedDate}
              onChange={setSelectedDate}
              onMonthChange={loadData}
              orientation="landscape"
              variant="static"
              openTo="date"
              renderDay={(
                day,
                selectedDate,
                isInCurrentMonth,
                dayComponent
              ) => {
                const active = dates.some((i) => isSameDay(day, parseISO(i)));

                // You can also use our internal <Day /> component
                return active && isInCurrentMonth ? (
                  <Tag>
                    {dayComponent} <span id="tag" />
                  </Tag>
                ) : (
                  dayComponent
                );
              }}
            />
          </ThemeProvider>
        </MuiPickersUtilsProvider>

        <Subtitle style={{ marginBottom: !infusions.length && 14 }}>
          Registro de infusão
        </Subtitle>

        <List>
          {infusions.map((item, index) => (
            <InfusionItem item={item} key={index} setActive={setActive} />
          ))}
        </List>
      </Content>

      {active && <InfusionShow item={active} />}

      <Backdrop open={loading}>
        <CircularProgress color="inherit" />
      </Backdrop>
    </Container>
  );
}

InfusionCalendar.propTypes = {
  user: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    register: PropTypes.number,
    created_at: PropTypes.string,
    updated_at: PropTypes.string,
  }).isRequired,
};

export default InfusionCalendar;
